import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  FileText, Clock, Users, MapPin, UserCheck, Paperclip, Bell,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import DateTimeField from '@/components/common/DateTimeField';
import { flashAutofilled } from '@/lib/flashAutofilled';
import { Badge } from '@/components/ui/badge';
import { calendarAPI, userAPI, agencyAPI, recognitionAPI } from '@/lib/api';
import { getErrorMessage, isKRRole, formatPhoneInput } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import VisibilityPreviewPanel from './VisibilityPreviewPanel';
import EntitySelector from '@/components/common/EntitySelector';
import AttachmentsPanel from './AttachmentsPanel';
import CoverImagePanel from './CoverImagePanel';
import DrawerSectionNav, { DrawerSection } from './DrawerSectionNav';
import LiveCaptureAgentPicker from './LiveCaptureAgentPicker';
import SalesAgentPicker from './SalesAgentPicker';
import {
  TIMEZONES, VISIBILITY_SCOPES, PRESETS, OWNER_OPTIONS,
  EVENT_TYPE_OPTIONS, BUSINESS_CATEGORY_OPTIONS, LANGUAGE_OPTIONS,
  blankEvent, fromRecord, wizardTypeByCategory,
  applyVisibilityScopeChange, buildPayload, validateForm,
} from './eventFormHelpers';

// Fields whose identity is frozen once an event is published.
const LOCKED_ON_PUBLISHED_UI = new Set(['title']);

const SECTIONS = [
  { id: 'general', label: 'General', icon: FileText },
  { id: 'schedule', label: 'Schedule', icon: Clock },
  { id: 'audience', label: 'Audience', icon: Users },
  { id: 'location', label: 'Location', icon: MapPin },
  { id: 'registration', label: 'Registration', icon: UserCheck },
  { id: 'attachments', label: 'Attachments', icon: Paperclip },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

export default function EventFormDrawer({
  open, mode, // 'create' | 'edit'
  event,
  categories,
  typeConfig = null,
  prefill = null,
  onClose,
  onSaved,
  onPublished,
  onCoverChanged,
}) {
  const [form, setForm] = useState(blankEvent);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewMode, setPreviewMode] = useState('saved');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savedDraftId, setSavedDraftId] = useState(null);
  const [savedRecord, setSavedRecord] = useState(null);
  const [dirtySinceSave, setDirtySinceSave] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [recognitionPrograms, setRecognitionPrograms] = useState([]);
  const initializedRef = useRef(false);

  // Phase 26.8.1 — owner facet UX differs by caller role.
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const callerAgencyId = useMemo(() => {
    if (isKR) return null;
    const ctx = user?.active_context;
    if (ctx?.type === 'agency') return ctx.id;
    const role = (user?.roles || []).find(
      (r) => (r.role === 'Agency_Principal' || r.role === 'Agency_User') && r.agency_id,
    );
    return role?.agency_id || null;
  }, [user, isKR]);
  const callerAgencyName = useMemo(() => {
    if (!callerAgencyId) return null;
    const ctx = user?.active_context;
    if (ctx?.type === 'agency' && ctx.id === callerAgencyId) return ctx.label || ctx.id;
    return callerAgencyId;
  }, [user, callerAgencyId]);

  const isEdit = mode === 'edit';
  const workingEventId = isEdit ? event?.event_id : savedDraftId;
  const workingStatus = isEdit ? event?.status : (savedRecord?.status || (savedDraftId ? 'draft' : null));
  const isPublished = workingStatus === 'published';

  // Reset the initialisation guard whenever the parent switches events
  // without closing the drawer — otherwise form state would stay stuck on
  // the previous event's data.
  useEffect(() => {
    initializedRef.current = false;
  }, [event?.event_id, mode]);

  // Initialise form when drawer opens.
  useEffect(() => {
    if (!open) { initializedRef.current = false; return; }
    if (initializedRef.current) return;
    initializedRef.current = true;
    if (isEdit && event) {
      setForm(fromRecord(event));
      setSavedRecord(event);
      setSavedDraftId(event.event_id);
    } else {
      const b = blankEvent();
      // Phase 26.8.1 — agency users create Internal + agency-owned events
      // by default. The owner_agency_id is stamped server-side too as
      // a defence in depth, but pre-filling here keeps the payload valid
      // and lets the visibility_scope dropdown behave correctly.
      if (!isKR && callerAgencyId) {
        b.owner_type = 'agency';
        b.owner_agency_id = callerAgencyId;
        // Also block Public up-front by defaulting to an Internal scope.
        b.visibility_scope = 'agency';
        b.audience_agency_id = callerAgencyId;
      }
      // Phase 27.x — seed from the creation wizard (type defaults + basics).
      const seeded = prefill ? applyVisibilityScopeChange({ ...b, ...prefill }, (prefill.visibility_scope || b.visibility_scope)) : b;
      setForm(seeded);
      setSavedRecord(null);
      setSavedDraftId(null);
    }
    setErrors({});
    setPreview(null);
    setPreviewMode('saved');
    setDirtySinceSave(false);
    setAttachments([]);
  }, [open, isEdit, event, isKR, callerAgencyId, prefill]);

  // Phase 26.8.1 — KR-only: agency directory for the Owner Agency picker.
  // Phase 27.29.8 — Also load the list for the visibility "agency" scope
  //                 so the audience_agency_id picker isn't a raw string.
  useEffect(() => {
    if (!isKR) return undefined;
    const needsList = form.owner_type === 'agency' || form.visibility_scope === 'agency';
    if (!needsList) return undefined;
    if (agencyOptions.length > 0) return undefined;
    let cancelled = false;
    agencyAPI.list()
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setAgencyOptions(
          list
            .filter((a) => a.status !== 'inactive')
            .map((a) => ({ value: a.agency_id, label: a.agency_name || a.agency_id })),
        );
      })
      .catch(() => { if (!cancelled) setAgencyOptions([]); });
    return () => { cancelled = true; };
  }, [isKR, form.owner_type, form.visibility_scope, agencyOptions.length]);

  // Load attachments when working event has an id.
  // Phase 26.9.3 — also depends on `open` so that reopening the drawer
  // for the same event re-fetches the latest attachments (cover uploads
  // committed in a previous session on the same event id would otherwise
  // stay stale because the [workingEventId] dep never changes).
  useEffect(() => {
    if (!open) return;
    if (!workingEventId) { setAttachments([]); return; }
    calendarAPI.listAttachments(workingEventId)
      .then((r) => setAttachments(r.data || []))
      .catch(() => setAttachments([]));
  }, [open, workingEventId]);

  // Phase 1b — recognition programs for the "Linked Recognition Program" picker.
  useEffect(() => {
    if (!open) return;
    recognitionAPI.listPrograms()
      .then((r) => setRecognitionPrograms(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setRecognitionPrograms([]));
  }, [open]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirtySinceSave(true);
  };
  const setNested = (parent, key, value) => {
    setForm((f) => ({ ...f, [parent]: { ...(f[parent] || {}), [key]: value } }));
    setDirtySinceSave(true);
  };
  const setVisibilityScope = (v) => {
    setForm((f) => applyVisibilityScopeChange(f, v));
    setDirtySinceSave(true);
  };

  const applyPreset = (key) => {
    const preset = PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const meaningfulKeys = ['title', 'description', 'internal_notes', 'audience_agency_id', 'virtual_meeting_url', 'contact_name', 'contact_phone', 'contact_email'];
    const hasContent = meaningfulKeys.some((k) => (form[k] || '').toString().trim() !== '')
      || (form.audience_user_ids || []).length > 0;
    if (hasContent) {
      const ok = window.confirm('Applying a preset will overwrite the current values. Continue?');
      if (!ok) return;
    }
    setForm((f) => {
      let next = { ...blankEvent(), ...preset.values };
      next.start_local = f.start_local;
      next.end_local = f.end_local;
      next.timezone = f.timezone || next.timezone;
      next = applyVisibilityScopeChange(next, next.visibility_scope);
      return next;
    });
    setDirtySinceSave(true);
    setPreview(null);
    toast.success(`Preset applied: ${preset.label}`);
  };

  const handleSaveDraft = async () => {
    const errs = validateForm(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error('Fix the highlighted fields before saving.');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(form, { excludeImmutable: false, isEdit });
      let saved;
      if (isEdit && workingEventId) {
        const patch = isPublished
          ? Object.fromEntries(Object.entries(payload).filter(([k]) => !LOCKED_ON_PUBLISHED_UI.has(k)))
          : payload;
        const res = await calendarAPI.updateEvent(workingEventId, patch);
        saved = res.data;
      } else if (savedDraftId) {
        const res = await calendarAPI.updateEvent(savedDraftId, payload);
        saved = res.data;
      } else {
        const res = await calendarAPI.createEvent(payload);
        saved = res.data;
      }
      setSavedRecord(saved);
      setSavedDraftId(saved.event_id);
      setDirtySinceSave(false);
      await runPreview({ againstSaved: true, id: saved.event_id });
      toast.success(isEdit ? 'Event updated' : 'Draft saved');
      onSaved?.(saved);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async ({ againstSaved, id }) => {
    const eventId = id || workingEventId;
    if (!eventId) {
      toast.error('Save the draft first to preview visibility.');
      return;
    }
    setPreviewLoading(true);
    try {
      const body = againstSaved ? {} : buildPayload(form, { excludeImmutable: isPublished });
      const res = await calendarAPI.previewVisibility(eventId, body);
      setPreview(res.data);
      setPreviewMode(againstSaved ? 'saved' : 'proposed');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Preview failed'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!workingEventId) {
      toast.error('Save the draft first.');
      return;
    }
    if (dirtySinceSave) {
      toast.error('Save your changes before publishing.');
      return;
    }
    setSaving(true);
    try {
      const res = await calendarAPI.publish(workingEventId);
      toast.success('Event published');
      setSavedRecord(res.data);
      onPublished?.(res.data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.publication_blocked) {
        setPreview({
          publication_ready: false,
          publication_blockers: detail.publication_blocked,
          warnings: ['Backend rejected publish — resolve blockers above'],
          effective_visibility: { scope: form.visibility_scope },
        });
        setPreviewMode('saved');
        toast.error('Publish blocked — see visibility preview');
      } else {
        toast.error(getErrorMessage(e, 'Publish failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const canPreview = !!workingEventId;
  const publicationReady = preview?.publication_ready === true;
  const canPublish = canPreview && !dirtySinceSave && publicationReady && !isPublished;

  const scopeLabel = useMemo(
    () => VISIBILITY_SCOPES.find((s) => s.value === form.visibility_scope)?.label,
    [form.visibility_scope],
  );

  const attachmentsBadge = attachments.length > 0
    ? <span className="text-[10px] font-semibold text-slate-500">{attachments.length}</span>
    : null;

  // Phase 27.x — the selected event type controls which sections/fields show.
  // Create mode: from the wizard (typeConfig). Edit mode: derived from the
  // event's legacy category. Null → show everything (legacy/ambiguous).
  const effType = useMemo(
    () => (isEdit ? wizardTypeByCategory(form.category) : typeConfig),
    [isEdit, form.category, typeConfig],
  );
  const showSection = (id) => !effType || (effType.sections || []).includes(id);
  const flags = effType?.flags || { leadCapture: true, carrier: true, npn: true, recognitionLink: false };

  const sections = SECTIONS
    .filter((s) => showSection(s.id))
    .map((s) =>
      s.id === 'attachments' ? { ...s, badge: attachmentsBadge, hidden: !workingEventId } : s,
    );

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent
        className="sm:max-w-4xl w-[90vw] p-0 flex flex-col"
        data-testid="event-form-drawer"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-200 shrink-0">
          <SheetHeader className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <SheetTitle data-testid="event-form-title">
                {isEdit ? 'Edit Event' : 'New Event'}
              </SheetTitle>
              {workingStatus && (
                <Badge className="text-[10px] uppercase" data-testid="event-form-status-badge">
                  {workingStatus}
                </Badge>
              )}
              {dirtySinceSave && (
                <span className="text-xs text-amber-700" data-testid="event-form-dirty-indicator">
                  · Unsaved changes
                </span>
              )}
            </div>
            <SheetDescription className="text-xs">
              {isEdit
                ? `Editing ${event?.status} event. Publication readiness is revalidated by the server on publish.`
                : 'Save as draft, preview visibility, then publish. Draft and Publish are separate steps.'}
              {isPublished && ' Title/slug are locked after publication.'}
            </SheetDescription>
          </SheetHeader>
        </div>

        {/* Body: left nav + scrollable content */}
        <div className="flex-1 min-h-0">
          <DrawerSectionNav sections={sections} testId="event-form-nav">
            {/* General ─────────────────────────────────────── */}
            <DrawerSection id="general" title="General" description="Basic identity, classification, and internal metadata.">
              {mode === 'create' && !savedDraftId && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Start from preset (optional)</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {PRESETS.map((p) => (
                      <Button
                        key={p.key}
                        variant="outline"
                        size="sm"
                        onClick={() => applyPreset(p.key)}
                        data-testid={`preset-${p.key}`}
                      >
                        {p.label}
                      </Button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Client-side convenience only. No template link is persisted.
                  </p>
                </div>
              )}

              <Field label="Title" required error={errors.title}>
                <Input
                  value={form.title}
                  onChange={(e) => setField('title', e.target.value)}
                  disabled={isPublished && LOCKED_ON_PUBLISHED_UI.has('title')}
                  data-testid="event-form-title-input"
                />
                {isPublished && (
                  <p className="text-[11px] text-slate-500 mt-1">Title is locked after publication (drives canonical slug).</p>
                )}
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Event Type">
                  <Select value={form.event_type || ''} onValueChange={(v) => { setField('event_type', v); if (v === 'informational') setField('requires_rsvp', false); }}>
                    <SelectTrigger data-testid="event-form-event-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">Canonical event type (Phase 26.1).</p>
                </Field>
                <Field label="Business Category">
                  <Select value={form.business_category || ''} onValueChange={(v) => setField('business_category', v)}>
                    <SelectTrigger data-testid="event-form-business-category"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {BUSINESS_CATEGORY_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">Line of business this event relates to.</p>
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Category (legacy)" required>
                  <Select value={form.category} onValueChange={(v) => setField('category', v)}>
                    <SelectTrigger data-testid="event-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.category} value={c.category}>{c.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">Legacy — kept for compatibility.</p>
                </Field>
                <Field label="Language">
                  <Select value={form.language || ''} onValueChange={(v) => setField('language', v)}>
                    <SelectTrigger data-testid="event-form-language"><SelectValue placeholder="Not specified" /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">Optional — language the event will be conducted in.</p>
                </Field>
              </div>

              {flags.carrier && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Carrier (informational)">
                    <Input
                      value={form.carrier_id}
                      onChange={(e) => setField('carrier_id', e.target.value)}
                      placeholder="carrier_id (optional)"
                      data-testid="event-form-carrier"
                    />
                  </Field>
                  <div />
                </div>
              )}

              <Field label="Description">
                <Textarea rows={4} value={form.description} onChange={(e) => setField('description', e.target.value)} data-testid="event-form-description" />
              </Field>
              <Field label="Internal notes (KR only)">
                <Textarea rows={3} value={form.internal_notes} onChange={(e) => setField('internal_notes', e.target.value)} data-testid="event-form-internal-notes" />
              </Field>
              {flags.leadCapture && (
                <>
                  <label className="flex items-start gap-2 mt-1 cursor-pointer" data-testid="event-form-live-capture">
                    <Checkbox
                      checked={!!form.live_capture_enabled}
                      onCheckedChange={(v) => setField('live_capture_enabled', !!v)}
                      className="mt-0.5"
                    />
                    <span className="text-sm">
                      <span className="font-medium text-slate-800">Available in Agent Live Capture</span>
                      <span className="block text-xs text-slate-500">Assigned agents can capture leads against this event from the Live Capture kiosk.</span>
                    </span>
                  </label>
                  {form.live_capture_enabled && (
                    <LiveCaptureAgentPicker
                      value={form.live_capture_agent_npns || []}
                      onChange={(v) => setField('live_capture_agent_npns', v)}
                    />
                  )}
                </>
              )}
              <label className="flex items-start gap-2 mt-1 cursor-pointer" data-testid="event-form-track-sales">
                <Checkbox
                  checked={!!form.track_sales}
                  onCheckedChange={(v) => setField('track_sales', !!v)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  <span className="font-medium text-slate-800">Track sales &amp; leads (Event Request)</span>
                  <span className="block text-xs text-slate-500">Register this event as an approved Event Request so its leads, conversations and conversion metrics show up in Event reports — even if it costs $0.</span>
                </span>
              </label>
              {form.track_sales && (
                <SalesAgentPicker
                  value={form.sales_agent_npns || []}
                  onChange={(v) => setField('sales_agent_npns', v)}
                />
              )}
              {(flags.recognitionLink || form.recognition_program_id) && (
                <Field label="Linked Recognition Program (optional)">
                  <Select value={form.recognition_program_id || '__none__'} onValueChange={(v) => setField('recognition_program_id', v === '__none__' ? '' : v)}>
                    <SelectTrigger data-testid="event-form-recognition-program"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {recognitionPrograms.map((p) => (
                        <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">Connect this event to a program in the Recognition section.</p>
                </Field>
              )}
            </DrawerSection>

            {/* Schedule ────────────────────────────────────── */}
            <DrawerSection id="schedule" title="Schedule" description="Start / end times are interpreted in the selected IANA timezone.">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start" required error={errors.start_local}>
                  <DateTimeField
                    value={form.start_local}
                    onChange={(v) => setField('start_local', v)}
                    testId="event-form-start"
                  />
                </Field>
                <Field label="End" required error={errors.end_local}>
                  <DateTimeField
                    value={form.end_local}
                    onChange={(v) => setField('end_local', v)}
                    testId="event-form-end"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Timezone" required error={errors.timezone}>
                  <Select value={form.timezone} onValueChange={(v) => setField('timezone', v)}>
                    <SelectTrigger data-testid="event-form-timezone"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (<SelectItem key={tz} value={tz}>{tz}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">Explicit — times are interpreted in this zone, not the browser&apos;s.</p>
                </Field>
                <Field label="All day">
                  <div className="flex items-center gap-2 h-9">
                    <Checkbox
                      checked={form.all_day}
                      onCheckedChange={(v) => setField('all_day', !!v)}
                      data-testid="event-form-all-day"
                    />
                    <span className="text-sm text-slate-700">Treat as full-day event</span>
                  </div>
                </Field>
              </div>
            </DrawerSection>

            {/* Audience ────────────────────────────────────── */}
            <DrawerSection id="audience" title="Audience" description="Who owns and can see this event.">
              {/* Phase 26.8.1 — Owner facet UX.
                    * Agency users: NEVER shown any owner controls. The
                      backend stamps owner_type='agency' + owner_agency_id
                      from the JWT context automatically.
                    * KR users: full choice + agency selector by name.
              */}
              {isKR ? (
                <>
                  <Field label="Owner" required error={errors.owner_type}>
                    <Select
                      value={form.owner_type || 'kr'}
                      onValueChange={(v) => {
                        setField('owner_type', v);
                        if (v === 'kr') setField('owner_agency_id', '');
                      }}
                    >
                      <SelectTrigger data-testid="event-form-owner-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OWNER_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Agency-owned events can never be Public.
                    </p>
                  </Field>

                  {form.owner_type === 'agency' && (
                    <Field label="Owner agency" required error={errors.owner_agency_id}>
                      <Select
                        value={form.owner_agency_id || ''}
                        onValueChange={(v) => setField('owner_agency_id', v)}
                      >
                        <SelectTrigger data-testid="event-form-owner-agency-select">
                          <SelectValue placeholder="Select agency…" />
                        </SelectTrigger>
                        <SelectContent>
                          {agencyOptions.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-slate-500">Loading agencies…</div>
                          ) : (
                            agencyOptions.map((a) => (
                              <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                </>
              ) : (
                // Agency-side compact confirmation — no editable owner fields.
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                  data-testid="event-form-owner-agency-locked"
                >
                  This event will be created as{' '}
                  <span className="font-semibold">Agency-owned</span> under{' '}
                  <span className="font-semibold">{callerAgencyName || 'your agency'}</span>. Agency users can create <strong>Internal</strong> events only.
                </div>
              )}

              <Field label="Visibility scope" required>
                <Select value={form.visibility_scope} onValueChange={setVisibilityScope}>
                  <SelectTrigger data-testid="event-form-visibility-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VISIBILITY_SCOPES
                      .filter((s) => (
                        // Agency callers OR KR when Owner=Agency ⇒ no Public option.
                        (isKR && form.owner_type !== 'agency') || s.value !== 'public'
                      ))
                      .map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">
                  {scopeLabel}
                  {" · "}
                  <span className={form.visibility_scope === 'public' ? 'text-emerald-700 font-semibold' : 'text-slate-600 font-semibold'}>
                    {form.visibility_scope === 'public' ? 'Public event' : 'Internal event'}
                  </span>
                </p>
              </Field>

              {form.visibility_scope === 'agency' && (
                <>
                  <Field label="Agency" required error={errors.audience_agency_id}>
                    <Select
                      value={form.audience_agency_id || ''}
                      onValueChange={(v) => setField('audience_agency_id', v)}
                    >
                      <SelectTrigger data-testid="event-form-agency-id">
                        <SelectValue placeholder="Choose the agency this event is for..." />
                      </SelectTrigger>
                      <SelectContent>
                        {agencyOptions.length === 0 ? (
                          <SelectItem value="__loading__" disabled>Loading agencies...</SelectItem>
                        ) : (
                          agencyOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </Field>
                  <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                    <Checkbox
                      checked={!!form.inherit_to_downline}
                      onCheckedChange={(v) => setField('inherit_to_downline', !!v)}
                      data-testid="event-form-inherit-downline"
                    />
                    <div className="text-sm">
                      <div className="font-medium text-slate-800">Include downline agencies</div>
                      <p className="text-xs text-slate-500">
                        When checked, agents and staff of every downline agency also see this event
                        and receive the publish notification. Uncheck to keep it visible only to the
                        selected agency&apos;s admins &amp; principals.
                      </p>
                    </div>
                  </div>
                </>
              )}

              {form.visibility_scope === 'specific_users' && (
                <Field label="Specific users" required error={errors.audience_user_ids}>
                  <EntitySelector
                    fetchPage={async ({ search, page, pageSize }) => {
                      const res = await userAPI.listPaginated({
                        search, page, page_size: pageSize, status: 'active',
                      });
                      return {
                        items: (res.data.items || []).map((u) => ({
                          value: u.user_id,
                          label: u.name || u.email,
                          sublabel: u.email,
                        })),
                        has_more: res.data.has_more,
                        total: res.data.total,
                      };
                    }}
                    value={(form.audience_user_ids || []).map((u) =>
                      typeof u === 'string' ? { value: u, label: u } : { value: u.user_id, label: u.label || u.user_id }
                    )}
                    onChange={(next) => setField(
                      'audience_user_ids',
                      next.map((n) => ({ user_id: n.value, label: n.label })),
                    )}
                    placeholder="Search users by name or email…"
                    testId="event-form-user-selector"
                  />
                </Field>
              )}

              {form.visibility_scope === 'public' && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={form.expose_meeting_url_publicly}
                    onCheckedChange={(v) => setField('expose_meeting_url_publicly', !!v)}
                    data-testid="event-form-expose-url"
                  />
                  <span className="text-sm text-slate-700">Expose virtual meeting URL publicly</span>
                </div>
              )}

              {canPreview && (
                <div className="mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Visibility preview</div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runPreview({ againstSaved: true })}
                      disabled={previewLoading}
                      data-testid="preview-saved-btn"
                    >
                      Preview saved state
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runPreview({ againstSaved: false })}
                      disabled={previewLoading}
                      data-testid="preview-proposed-btn"
                    >
                      Preview proposed changes
                    </Button>
                  </div>
                  <VisibilityPreviewPanel preview={preview} mode={previewMode} loading={previewLoading} />
                </div>
              )}
            </DrawerSection>

            {/* Location ────────────────────────────────────── */}
            <DrawerSection id="location" title="Location" description="Physical address and/or virtual meeting URL.">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Street" className="col-span-2">
                  <AddressAutocomplete
                    value={form.physical_location.street}
                    onChange={(v) => setNested('physical_location', 'street', v)}
                    onPlaceSelected={(parsed) => {
                      setForm((f) => ({
                        ...f,
                        physical_location: {
                          ...(f.physical_location || {}),
                          street: parsed.street || parsed.formatted,
                          city: parsed.city || f.physical_location?.city || '',
                          state: parsed.state || f.physical_location?.state || '',
                          zip: parsed.zip || f.physical_location?.zip || '',
                        },
                      }));
                      setDirtySinceSave(true);
                      flashAutofilled(['event-form-city', 'event-form-state', 'event-form-zip']);
                    }}
                    testId="event-form-street"
                  />
                </Field>
                <Field label="City">
                  <Input value={form.physical_location.city} onChange={(e) => setNested('physical_location', 'city', e.target.value)} data-testid="event-form-city" />
                </Field>
                <Field label="State">
                  <Input value={form.physical_location.state} onChange={(e) => setNested('physical_location', 'state', e.target.value)} data-testid="event-form-state" />
                </Field>
                <Field label="ZIP">
                  <Input value={form.physical_location.zip} onChange={(e) => setNested('physical_location', 'zip', e.target.value)} data-testid="event-form-zip" />
                </Field>
              </div>
              <Field label="Virtual meeting URL">
                <Input value={form.virtual_meeting_url} onChange={(e) => setField('virtual_meeting_url', e.target.value)} placeholder="https://…" data-testid="event-form-meeting-url" />
              </Field>
            </DrawerSection>

            {/* Registration ────────────────────────────────── */}
            {showSection('registration') && (
            <DrawerSection id="registration" title="Registration" description="Contact info, capacity, deadline, and guest configuration.">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Contact name">
                  <Input value={form.contact_name} onChange={(e) => setField('contact_name', e.target.value)} data-testid="event-form-contact-name" />
                </Field>
                <Field label="Contact phone">
                  <Input value={formatPhoneInput(form.contact_phone)} onChange={(e) => setField('contact_phone', e.target.value)} data-testid="event-form-contact-phone" />
                </Field>
                <Field label="Contact email">
                  <Input value={form.contact_email} onChange={(e) => setField('contact_email', e.target.value)} data-testid="event-form-contact-email" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Capacity">
                  <Input
                    type="number"
                    min={1}
                    value={form.capacity}
                    onChange={(e) => setField('capacity', e.target.value)}
                    data-testid="event-form-capacity"
                  />
                </Field>
                <Field label="Registration deadline">
                  <DateTimeField
                    value={form.registration_deadline_local}
                    onChange={(v) => setField('registration_deadline_local', v)}
                    testId="event-form-registration-deadline"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Optional — interpreted in the selected timezone.</p>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Requires RSVP">
                  <div className="flex items-center gap-2 h-9">
                    <Checkbox checked={form.requires_rsvp && form.event_type !== 'informational'} disabled={form.event_type === 'informational'} onCheckedChange={(v) => setField('requires_rsvp', !!v)} data-testid="event-form-rsvp" />
                    <span className="text-sm text-slate-700">{form.event_type === 'informational' ? 'Non-registerable (informational) — RSVP disabled' : 'Participants must RSVP'}</span>
                  </div>
                </Field>
                <Field label="Allow late registration">
                  <div className="flex items-center gap-2 h-9">
                    <Checkbox checked={form.allow_late_registration} onCheckedChange={(v) => setField('allow_late_registration', !!v)} data-testid="event-form-allow-late" />
                    <span className="text-sm text-slate-700">Accept registrations after the deadline</span>
                  </div>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Allow guests">
                  <div className="flex items-center gap-2 h-9">
                    <Checkbox checked={form.registration_allow_guests} onCheckedChange={(v) => setField('registration_allow_guests', !!v)} data-testid="event-form-allow-guests" />
                    <span className="text-sm text-slate-700">Registrants may bring guests</span>
                  </div>
                </Field>
                <Field label="Max guests per registration">
                  <Input
                    type="number"
                    min={0}
                    max={20}
                    value={form.max_guests_per_registration}
                    onChange={(e) => setField('max_guests_per_registration', e.target.value)}
                    disabled={!form.registration_allow_guests}
                    data-testid="event-form-max-guests"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Each guest consumes 1 seat from capacity.</p>
                </Field>
              </div>
              <Field label="Requires NPN to register">
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.requires_npn_to_register}
                    onCheckedChange={(v) => setField('requires_npn_to_register', !!v)}
                    className="mt-0.5"
                    data-testid="event-form-requires-npn"
                  />
                  <div>
                    <div className="text-sm text-slate-700">
                      Ask for an NPN on the public share-link form
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Enable for trainings, certifications, and other agent-only
                      events. When on, share-link registrants must provide an
                      NPN — recognised NPNs join as agents, unrecognised NPNs
                      are captured as Prospects. Leave off for public events
                      (parties, celebrations) where anyone can register with
                      just a name.
                    </p>
                  </div>
                </label>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Enable Waitlist">
                  <div className="flex items-center gap-2 h-9">
                    <Checkbox checked={form.waitlist_enabled} onCheckedChange={(v) => setField('waitlist_enabled', !!v)} data-testid="event-form-waitlist-enabled" />
                    <span className="text-sm text-slate-700">Waitlist new registrations when full (requires capacity)</span>
                  </div>
                </Field>
                <Field label="Accept Waitlist">
                  <div className="flex items-center gap-2 h-9">
                    <Checkbox checked={form.waitlist_accepting} onCheckedChange={(v) => setField('waitlist_accepting', !!v)} disabled={!form.waitlist_enabled} data-testid="event-form-waitlist-accepting" />
                    <span className="text-sm text-slate-700">Currently accepting waitlist entries</span>
                  </div>
                </Field>
              </div>
            </DrawerSection>
            )}
            {showSection('attachments') && (
            <DrawerSection id="attachments" title="Attachments" description="Flyers, agendas, resource sheets. Public flyers require public event visibility.">
              {workingEventId && savedRecord ? (
                <>
                  {/* Phase 26.9 — Cover image (single hero banner per event) */}
                  <CoverImagePanel
                    event={savedRecord}
                    attachments={attachments}
                    onChanged={async () => {
                      try {
                        const r = await calendarAPI.listAttachments(workingEventId);
                        setAttachments(r.data || []);
                      } catch { /* best-effort refresh */ }
                      // Phase 26.9.3 — notify the parent (calendar grid /
                      // events list) so the visible card thumbnail refetches
                      // its cover URL immediately, without requiring a Save
                      // or a page reload.
                      onCoverChanged?.(workingEventId);
                    }}
                  />
                  <AttachmentsPanel
                    event={savedRecord}
                    attachments={attachments}
                    canManage
                    isKR
                    onChanged={async () => {
                      try {
                        const r = await calendarAPI.listAttachments(workingEventId);
                        setAttachments(r.data || []);
                      } catch {
                        // best-effort refresh
                      }
                    }}
                  />
                </>
              ) : (
                <p className="text-xs text-slate-500">Save the draft first — attachments become available after an event has an ID.</p>
              )}
            </DrawerSection>
            )}

            {/* Notifications ─────────────────────────────── */}
            <DrawerSection id="notifications" title="Notifications" description="Transactional email settings for this event. Reminders use the event timezone.">
              <div className="space-y-3">
                <label className="flex items-start gap-2 cursor-pointer" data-testid="notif-master-toggle">
                  <Checkbox checked={form.email_notifications_enabled} onCheckedChange={(v) => setField('email_notifications_enabled', !!v)} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">Email notifications enabled</div>
                    <div className="text-[11px] text-slate-500">Master switch. When off, no emails are sent for this event.</div>
                  </div>
                </label>
                {form.email_notifications_enabled && (
                  <div className="pl-6 space-y-2">
                    <NotifRow testId="notif-reg-conf" label="Registration confirmation" checked={form.send_registration_confirmation} onChange={(v) => setField('send_registration_confirmation', v)} />
                    <NotifRow testId="notif-wl" label="Waitlist join & promotion" checked={form.send_waitlist_notifications} onChange={(v) => setField('send_waitlist_notifications', v)} />
                    <NotifRow testId="notif-updates" label="Event update / cancellation" checked={form.send_event_update_notifications} onChange={(v) => setField('send_event_update_notifications', v)} />
                    <NotifRow testId="notif-24h" label="Reminder — 24 hours before" checked={form.send_reminder_24h} onChange={(v) => setField('send_reminder_24h', v)} />
                    <NotifRow testId="notif-1h" label="Reminder — 1 hour before" checked={form.send_reminder_1h} onChange={(v) => setField('send_reminder_1h', v)} />
                  </div>
                )}
                {savedRecord?.notification_meta && (
                  <div className="mt-2 pt-2 border-t border-slate-200 text-[11px] text-slate-500">
                    Delivery history is available in the Event Detail drawer.
                  </div>
                )}
              </div>
            </DrawerSection>
          </DrawerSectionNav>
        </div>

        {/* Sticky footer actions */}
        <div className="px-6 py-3 bg-white border-t border-slate-200 flex items-center gap-2 justify-end shrink-0" data-testid="event-form-footer">
          <Button variant="ghost" onClick={onClose} data-testid="event-form-close">Close</Button>
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={saving}
            data-testid="event-form-save-draft"
          >
            {isEdit ? 'Save changes' : (savedDraftId ? 'Update draft' : 'Save draft')}
          </Button>
          {workingStatus === 'draft' && (
            <Button
              onClick={handlePublish}
              disabled={saving || !canPublish}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="event-form-publish"
              title={
                !workingEventId ? 'Save a draft first' :
                dirtySinceSave ? 'Save changes first' :
                !publicationReady ? 'Resolve blockers first' : ''
              }
            >
              Publish
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const Field = ({ label, required, error, children }) => (
  <div>
    <Label className="text-xs text-slate-600">
      {label}{required && <span className="text-rose-600"> *</span>}
    </Label>
    <div className="mt-1">{children}</div>
    {error && <p className="text-[11px] text-rose-600 mt-1" data-testid="event-form-field-error">{error}</p>}
  </div>
);

const NotifRow = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 cursor-pointer" data-testid={testId}>
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
    <span className="text-sm text-slate-700">{label}</span>
  </label>
);
